import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { UserDao } from './dao/userDao';
import { AdvertiserDao } from './dao/advertiserDao';
import { SingletonDao } from './dao/singletonDao';
import { User } from '../models/user';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private user: User | null = null;

  private exceptionsEnabled$ = new BehaviorSubject<boolean>(false);
  private exceptionsLoaded = false; // prevent duplicate fetches

  constructor(
    private userDao: UserDao,
    private advertiserDao: AdvertiserDao,
    private singletonDao: SingletonDao
  ) {
    this.refreshUser();         // cheap local read is fine at startup
    // DO NOT call loadExceptionsFlag() here — API base/auth may not be ready yet.
  }

  refreshUser() {
    this.user = this.userDao.getCurrentUser();
  }

  /** Call this AFTER env + login are done (see AppComponent section) */
  async initAfterLogin(): Promise<void> {
    if (this.exceptionsLoaded) return;
    await this.loadExceptionsFlag();
    this.exceptionsLoaded = true;
  }

  private async loadExceptionsFlag(): Promise<void> {
    try {
      const rows = await firstValueFrom(
        this.singletonDao.find({ key: 'detect_exceptions_enabled' })
      );
      const enabled = rows?.[0]?.data?.['status'] === 'true';
      this.exceptionsEnabled$.next(!!enabled);
    } catch (err) {
      console.error('Failed to load exceptions flag', err);
      this.exceptionsEnabled$.next(false);
    }
  }

  // ---- role helpers ----
  get isAdmin(): boolean { return !!this.user?.isAdmin; }
  get isAuthUser(): boolean { return !!this.user?.roles?.some(r => r.type === 'Authenticated'); }
  get isAdvManager(): boolean { return !!this.user?.roles?.some(r => r.type === 'AdvertiserManager'); }
  get isChangeManagement(): boolean { return !!this.user?.roles?.some(r => r.type === 'ChangeManagement'); }
  get isExceptionsUser(): boolean { return !!this.user?.roles?.some(r => r.type === 'Exceptions'); }
  get isAdCodeManager(): boolean { return !!this.user?.roles?.some(r => r.type === 'AdCodeMetadataManager'); }
  get isDetectionReportsUser(): boolean { return !!this.user?.roles?.some(r => r.type === 'DetectionReports'); }

  /** Admin + selected advertiser means impersonating */
  get isImpersonating(): boolean {
    return this.isAdmin && !!this.advertiserDao.getCurrentAdvertiser();
  }

  // ---- permissions ----
  canAccessMenuItem(): boolean {
    return !this.isAdmin || this.isImpersonating;
  }
  canAccessAuthItem(): boolean {
    return this.isAuthUser || this.isAdmin;
  }
  canAccessDetectionReports(): boolean {
    return this.isDetectionReportsUser || this.isAdCodeManager || this.isAdvManager || this.isImpersonating;
  }
  canAccessAdvManagerItem(): boolean {
    return this.isAdvManager || this.isImpersonating;
  }
  canAccessChangeManagementItem(): boolean {
    return this.isChangeManagement || this.isImpersonating;
  }
  isAdminOrAdvManager(): boolean {
    return this.isAdmin || this.isAdvManager;
  }
  canAccessExceptionsItem(): boolean {
    return this.exceptionsEnabled$.value && (this.isExceptionsUser || this.isImpersonating);
  }
  canAccessLookerItem(group: string): boolean {
    if (group === 'Dashboards' || group === 'Channel Monitoring') return this.canAccessDetectionReports();
    if (group === 'In-Flight') return this.canAccessExceptionsItem();
    return false;
  }
  canAccessAdCodes(): boolean {
    return this.isAdvManager || this.isAdCodeManager || this.isImpersonating;
  }
  canAccessEncoder(brand: any): boolean {
    if (!brand) return false;
    const hasBvs = !!brand.bvsCustomerName;
    const isBvsOnly = !!this.user?.bvsOnly;

    return (hasBvs && isBvsOnly) || this.isImpersonating || this.isAdmin;
  }
}
